import { Reducer } from 'redux'
import { IRecentFacilitiesState } from 'apps/fleet-planner/types/facilities'
import {
  GET_RECENT_FACILITIES_ACTION_TYPES,
  CREATE_NEW_FACILITY_ACTION_TYPES,
  UPDATE_FACILITY_ACTION_TYPES,
  UPDATE_FACILITY_ADDRESS_ACTION_TYPES,
  UPDATE_FACILITY_CONTACT_ACTION_TYPES,
  COMPLEX_UPDATE_FACILITY_ACTION_TYPES,
  CREATE_FACILITY_CONTACT_ACTION_TYPES,
} from 'apps/fleet-planner/utils/actions/facilities'
import { IFacility } from 'apps/fleet-planner/utils/api/facilities'
import {
  FacilitiesActions,
  ICreateFacilityContactSuccessAction,
  ICreateNewFacilitySuccessAction,
  IGetRecentFacilitiesFailureAction,
  IGetRecentFacilitiesSuccessAction,
  IUpdateFacilityAddressSuccessAction,
  IUpdateFacilityContactSuccessAction,
  IUpdateFacilitySuccessAction,
} from '../types/facilities'

const {
  GET_RECENT_FACILITIES_FAILURE,
  GET_RECENT_FACILITIES_REQUEST,
  GET_RECENT_FACILITIES_SUCCESS,
} = GET_RECENT_FACILITIES_ACTION_TYPES

const {
  CREATE_NEW_FACILITY_FAILURE,
  CREATE_NEW_FACILITY_REQUEST,
  CREATE_NEW_FACILITY_SUCCESS,
} = CREATE_NEW_FACILITY_ACTION_TYPES

const {
  UPDATE_FACILITY_FAILURE,
  UPDATE_FACILITY_REQUEST,
  UPDATE_FACILITY_SUCCESS,
} = UPDATE_FACILITY_ACTION_TYPES

const {
  UPDATE_FACILITY_ADDRESS_FAILURE,
  UPDATE_FACILITY_ADDRESS_REQUEST,
  UPDATE_FACILITY_ADDRESS_SUCCESS,
} = UPDATE_FACILITY_ADDRESS_ACTION_TYPES

const {
  UPDATE_FACILITY_CONTACT_FAILURE,
  UPDATE_FACILITY_CONTACT_REQUEST,
  UPDATE_FACILITY_CONTACT_SUCCESS,
} = UPDATE_FACILITY_CONTACT_ACTION_TYPES

const {
  CREATE_FACILITY_CONTACT_FAILURE,
  CREATE_FACILITY_CONTACT_REQUEST,
  CREATE_FACILITY_CONTACT_SUCCESS,
} = CREATE_FACILITY_CONTACT_ACTION_TYPES

const {
  COMPLEX_UPDATE_FACILITY_FAILURE,
  COMPLEX_UPDATE_FACILITY_REQUEST,
  COMPLEX_UPDATE_FACILITY_SUCCESS,
} = COMPLEX_UPDATE_FACILITY_ACTION_TYPES

const defaultState: IRecentFacilitiesState = {
  fetching: false,
  isMultiRequestLoading: false,
  error: undefined,
  facilities: [],
}

export const recentFacilitiesReducer: Reducer<IRecentFacilitiesState, FacilitiesActions> = (
  state = defaultState,
  action
) => {
  switch (action.type) {
    case UPDATE_FACILITY_ADDRESS_REQUEST:
    case CREATE_NEW_FACILITY_REQUEST:
    case GET_RECENT_FACILITIES_REQUEST:
    case UPDATE_FACILITY_CONTACT_REQUEST:
    case CREATE_FACILITY_CONTACT_REQUEST:
    case UPDATE_FACILITY_REQUEST: {
      return {
        ...state,
        error: undefined,
        fetching: true,
      }
    }
    case UPDATE_FACILITY_ADDRESS_FAILURE:
    case CREATE_NEW_FACILITY_FAILURE:
    case GET_RECENT_FACILITIES_FAILURE:
    case UPDATE_FACILITY_CONTACT_FAILURE:
    case CREATE_FACILITY_CONTACT_FAILURE:
    case UPDATE_FACILITY_FAILURE: {
      const { error } = action as IGetRecentFacilitiesFailureAction
      return {
        ...state,
        fetching: false,
        error,
      }
    }
    case GET_RECENT_FACILITIES_SUCCESS: {
      const {
        payload: { facilities = [] },
      } = action as IGetRecentFacilitiesSuccessAction
      return {
        ...state,
        error: undefined,
        fetching: false,

        // TODO: clean up this filter
        facilities: facilities
          .filter((facility: IFacility) => {
            const hasAddresses = facility.addresses.length > 0
            const hasValidAddress = hasAddresses && !!facility.addresses[0].addressLine1
            return hasAddresses && hasValidAddress
          })
          .sort((a: IFacility, b: IFacility) => parseInt(a.id, 10) - parseInt(b.id, 10)),
      }
    }
    case CREATE_NEW_FACILITY_SUCCESS: {
      const { payload } = action as ICreateNewFacilitySuccessAction
      const { facility } = payload || {}
      return {
        ...state,
        fetching: false,
        facilities: [...state.facilities, facility],
      }
    }
    case UPDATE_FACILITY_SUCCESS: {
      const { payload } = action as IUpdateFacilitySuccessAction
      const { facility } = payload || {}
      return {
        ...state,
        fetching: false,
        facilities: [...state.facilities.map(f => (f.id === facility.id ? facility : f))],
      }
    }
    case UPDATE_FACILITY_ADDRESS_SUCCESS: {
      const { payload } = action as IUpdateFacilityAddressSuccessAction
      const { address } = payload || {}

      const prevFacility = state.facilities.filter(f => f.id === address.addressableId)[0]
      const newFacility = {
        ...prevFacility,
        addresses: prevFacility?.addresses.map(a => (a.id === address.id ? address : a)),
      }

      return {
        ...state,
        fetching: false,
        facilities: [
          ...state.facilities.map(f => (f.id === address.addressableId ? newFacility : f)),
        ],
      }
    }
    case UPDATE_FACILITY_CONTACT_SUCCESS: {
      const { payload } = action as IUpdateFacilityContactSuccessAction
      const { contact } = payload || {}

      const prevFacility = state.facilities.filter(f => f.id === contact.contactableId)[0]
      const newFacility = {
        ...prevFacility,
        contacts: prevFacility?.contacts.map(a => (a.id === contact.contactableId ? contact : a)),
      }

      return {
        ...state,
        fetching: false,
        facilities: [
          ...state.facilities.map(f => (f.id === contact.contactableId ? newFacility : f)),
        ],
      }
    }
    case CREATE_FACILITY_CONTACT_SUCCESS: {
      const { payload } = action as ICreateFacilityContactSuccessAction
      const { contact } = payload || {}

      const prevFacility = state.facilities.filter(f => f.id === contact.contactableId)[0]
      const { contacts = [] } = prevFacility || {}
      const newFacility = {
        ...prevFacility,
        contacts: [...contacts, contact],
      }

      return {
        ...state,
        fetching: false,
        facilities: [
          ...state.facilities.map(f => (f.id === contact.contactableId ? newFacility : f)),
        ],
      }
    }
    case COMPLEX_UPDATE_FACILITY_REQUEST: {
      return {
        ...state,
        error: undefined,
        isMultiRequestLoading: true,
      }
    }
    case COMPLEX_UPDATE_FACILITY_SUCCESS:
    case COMPLEX_UPDATE_FACILITY_FAILURE: {
      // Errors will be handled by specific functions inside this function
      return {
        ...state,
        isMultiRequestLoading: false,
      }
    }
    default:
      return state
  }
}
